import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';

import { NgbAccordionModule, NgbCollapseModule, NgbDatepickerI18n, NgbDatepickerModule, NgbDropdownModule, NgbNavModule, NgbPopoverModule, NgbToastModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { PageNotFoundComponent } from './components/';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProfileHeaderComponent } from './components/profile-header/profile-header.component';
import { LaddaModule } from 'angular2-ladda';
import { CallHistoryTypePipe, HightlightPipe, MatchingNumbersCountPipe, RecordFullnamePipe, RecordNamePipe, SecondsToMinutesPipe, TimesPipe } from './pipes';
import { MainActionButtonComponent } from './components/main-action-button/main-action-button.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { FavoriteIconComponent } from './components/favorite-icon/favorite-icon.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ConfirmationComponent } from './components/notifications/confirmation/confirmation.component';
import { ReferralService } from '../settings/services/referral/referral.service';
import { DialerButtonComponent } from './components/dialer-button/dialer-button.component';
import { DialerComponent } from './components/dialer/dialer.component';
import { StartCallModalComponent } from './components/start-call-modal/start-call-modal.component';
import { InAppCallNotificationComponent } from './components/in-app-call-notification/in-app-call-notification.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { CallDirectionIconComponent } from './components/call-direction-icon/call-direction-icon.component';
import { ContactDetailsModalComponent } from './components/contact-details-modal/contact-details-modal.component';
import { ReconnectionModalComponent } from './components/reconnection-modal/reconnection-modal.component';
import { SpyCallButtonComponent } from './components/spy-call-button/spy-call-button.component';
import { StoreModule } from '@ngrx/store';
import { userReducer } from './stores/user/reducer/user.reducer';
import { CallButtonComponent } from './components/call-button/call-button.component';
import { ChatInboxNotificationComponent } from './components/chat-inbox-notification/chat-inbox-notification.component';
import { MainInAppNotificationsComponent } from './components/main-in-app-notifications/main-in-app-notifications.component';
import { AddEditContactModalComponent } from '../contacts/add-edit-contact-modal/add-edit-contact-modal.component';
import { VoxAddressSearchModule, VoxAudioPlayerModule, VoxCountrySelectorModule, VoxDropdownMultiselectModule, VoxPhoneTextFieldModule, VoxTimezoneInputModule, VoxToggleModule } from 'ng-voxloud';
import { AddContactQuickComponent } from './components/add-contact-quick/add-contact-quick.component';
import { AddToExistingContactModalComponent } from './components/add-to-existing-contact-modal/add-to-existing-contact-modal.component';
import { AddEditContactFormComponent } from './components/add-edit-contact-form/add-edit-contact-form.component';
import { CompanyCallHistoryTableComponent } from './components/company-call-history-table/company-call-history-table.component';
import { SentimentScoreComponent } from './components/sentiment-score/sentiment-score.component';
import { CompanyCallHistoryDetailComponent } from './components/company-call-history-detail/company-call-history-detail.component';
import { GeneralListComponent } from './components/company-call-history-detail/general-list/general-list.component';
import { AiListComponent } from './components/company-call-history-detail/ai-list/ai-list.component';
import { WebhookListComponent } from './components/company-call-history-detail/webhook-list/webhook-list.component';
import { CallRecordingComponent } from './components/company-call-history-detail/call-recording/call-recording.component';
import { VoicemailComponent } from './components/company-call-history-detail/voicemail/voicemail.component';
import { CompanyCallHistoryFiltersComponent } from './components/company-call-history-filters/company-call-history-filters.component';
import { FilterTextComponent } from './components/company-call-history-filters/filter-text/filter-text.component';
import { FilterDropdownComponent } from './components/company-call-history-filters/filter-dropdown/filter-dropdown.component';
import { FilterUsersComponent } from './components/company-call-history-filters/filter-users/filter-users.component';
import { FilterIntervalComponent } from './components/company-call-history-filters/filter-interval/filter-interval.component';
import { FilterDidComponent } from './components/company-call-history-filters/filter-did/filter-did.component';
import { FilterDateTimeComponent } from './components/company-call-history-filters/filter-date-time/filter-date-time.component';
import { FilterDateComponent } from './components/company-call-history-filters/filter-date/filter-date.component';
import { FilterTimeComponent } from './components/company-call-history-filters/filter-time/filter-time.component';
import { FilterDateTimeRangeComponent } from './components/company-call-history-filters/filter-date-time-range/filter-date-time-range.component';
import { FilterTimeRangeComponent } from './components/company-call-history-filters/filter-time-range/filter-time-range.component';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { ClickOutsideDirective } from './directives/clickoutside.directive';
import { CustomDatePickerTranslateService } from './services/custom-datepicker-translate/custom-date-picker-translate.service';
import { FilterModalComponent } from './components/company-call-history-filters/filter-modal/filter-modal.component';
import { CompanyCallHistoryGeneralSettingsComponent } from './components/company-call-history-general-settings/company-call-history-general-settings.component';
import { CompanyCallHistoryTagsManagmentComponent } from './components/company-call-history-tags-managment/company-call-history-tags-managment.component';
import { TagInputComponent } from './components/company-call-history-tags-managment/components/tag-input/tag-input.component';
import { FilterTagComponent } from './components/company-call-history-filters/filter-tag/filter-tag.component';
import { SmsCallDetailsComponent } from './components/company-call-history-detail/sms-details/sms-call-details.component';
import { ClipboardBoxComponent } from './components/clipboard-box/clipboard-box.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ContactSearchComponent } from './components/contact-search/contact-search.component';
import { OutboundDidSelectComponent } from './components/outbound-did-select/outbound-did-select.component';

const COMPONENTS = [
  PageNotFoundComponent,
  ProfileHeaderComponent,
  MainActionButtonComponent,
  AvatarComponent,
  FavoriteIconComponent,
  ConfirmationComponent,
  DialerButtonComponent,
  DialerComponent,
  StartCallModalComponent,
  InAppCallNotificationComponent,
  PaginationComponent,
  CallDirectionIconComponent,
  ContactDetailsModalComponent,
  ReconnectionModalComponent,
  SpyCallButtonComponent,
  CallButtonComponent,
  ChatInboxNotificationComponent,
  MainInAppNotificationsComponent,
  AddEditContactModalComponent,
  AddContactQuickComponent,
  AddEditContactFormComponent,
  AddContactQuickComponent,
  AddToExistingContactModalComponent,
  CompanyCallHistoryTableComponent,
  SentimentScoreComponent,
  CompanyCallHistoryDetailComponent,
  GeneralListComponent,
  AiListComponent,
  WebhookListComponent,
  SmsCallDetailsComponent,
  CallRecordingComponent,
  VoicemailComponent,
  CompanyCallHistoryFiltersComponent,
  FilterTextComponent,
  FilterDropdownComponent,
  FilterUsersComponent,
  FilterIntervalComponent,
  FilterDidComponent,
  FilterDateTimeComponent,
  FilterDateComponent,
  FilterTimeComponent,
  FilterDateTimeRangeComponent,
  FilterTimeRangeComponent,
  FilterModalComponent,
  CompanyCallHistoryGeneralSettingsComponent,
  CompanyCallHistoryTagsManagmentComponent,
  TagInputComponent,
  FilterTagComponent,
  ClipboardBoxComponent,
  ContactSearchComponent,
  OutboundDidSelectComponent
]

const PIPES = [
  TimesPipe,
  CallHistoryTypePipe,
  RecordNamePipe,
  SecondsToMinutesPipe,
  HightlightPipe,
  RecordFullnamePipe,
  MatchingNumbersCountPipe
]

const DIRECTIVES = [
  AutofocusDirective,
  ClickOutsideDirective
]

const MODULES = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  VoxAddressSearchModule, 
  VoxCountrySelectorModule, 
  VoxPhoneTextFieldModule, 
  VoxTimezoneInputModule,
  VoxDropdownMultiselectModule,
  VoxToggleModule,
  VoxAudioPlayerModule,
  TimepickerModule.forRoot(),
  TooltipModule.forRoot()
]

const EXPORT_MODULES = [
  TranslateModule,
  FormsModule,
  LaddaModule,
  ReactiveFormsModule,
  NgxSkeletonLoaderModule,
  NgbDropdownModule,
  NgbDatepickerModule,
  NgbPopoverModule,
  NgbTooltipModule,
  NgbAccordionModule,
  NgbCollapseModule,
  NgbToastModule,
  NgbNavModule
]

const SERVICES = [
  ReferralService,
  {
    provide: NgbDatepickerI18n,
    useClass: CustomDatePickerTranslateService
  }
]

@NgModule({
  declarations: [
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES
  ],
  imports: [
    ...MODULES,
    ...EXPORT_MODULES
  ],
  providers: [
    ...SERVICES,
    ...PIPES
  ],
  exports: [
    ...EXPORT_MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES
  ]
})
export class SharedModule { }
